import { Badge } from '../components';
import { isFeedbackKind, type Thread } from './types';

type Variant = 'default' | 'success' | 'warning' | 'error' | 'info';

/**
 * A student–teacher thread's status, said from the reader's side.
 *
 * The stored status is the owner's: `replied` means "the teacher has spoken,
 * the student is next". That reads right on a doubt, but on feedback the
 * teacher opened nothing was replied to, and a teacher seeing their own
 * feedback as "Replied" is backwards. So the label says whose move it is, in
 * the reader's words. Closed states read the same for everyone.
 */
export function studentTeacherStatus(thread: Thread, viewer: 'student' | 'teacher'): { label: string; variant: Variant } {
    if (thread.status === 'resolved') return { label: 'Resolved', variant: 'default' };
    if (thread.status === 'closed' || thread.status === 'wont_fix') return { label: 'Closed', variant: 'default' };

    const studentIsNext = thread.status === 'replied';
    const feedback = isFeedbackKind(thread.kind);
    const studentHasWritten = !feedback || thread.messages.some(m => m.senderId === thread.createdBy);
    const teacherHasWritten = thread.messages.some(m => m.senderId === thread.ownerId);

    if (viewer === 'student') {
        if (!studentIsNext) return { label: 'Sent', variant: 'info' };
        return studentHasWritten
            ? { label: 'Teacher replied', variant: 'success' }
            : { label: 'From teacher', variant: 'success' };
    }
    if (studentIsNext) return { label: 'Sent', variant: 'info' };
    return teacherHasWritten
        ? { label: 'Student replied', variant: 'warning' }
        : { label: 'New doubt', variant: 'warning' };
}

export function StudentTeacherStatusBadge({ thread, viewer }: { thread: Thread; viewer: 'student' | 'teacher' }) {
    const { label, variant } = studentTeacherStatus(thread, viewer);
    return <Badge variant={variant} size="sm">{label}</Badge>;
}
